import {FC, RefObject, useState} from 'react';
import {api, IMemoryNote, useT, getDateTimeString} from '@kcast-fe/core';
import {QuickMenu, Svg, ConfirmDialog, useScreenSize} from 'ui-kit';
import {Box, Flex} from '@chakra-ui/react';

interface IProps {
  note: IMemoryNote;
  containerRef: RefObject<HTMLDivElement | null>;
  onEdit: (note: IMemoryNote) => void;
}

const NoteItemFC: FC<IProps> = ({note, containerRef, onEdit}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const deleteMutation = api.memoryNotes.useDeleteNoteMutation();

  const {t} = useT();
  const {isLessThan768} = useScreenSize();

  const handleDelete = () => {
    deleteMutation.mutate(note.id, {onSuccess: () => setConfirmDelete(false)});
  };

  return (
    <>
      <Flex
        align={'flex-start'}
        gap={'8px'}
        p={'16px 0 12px 4px'}
        borderBottom={'thin solid'}
        borderColor={'{colors.borderDefault}'}
        className={'group'}
        _hover={{bg: '{colors.bgSurface}'}}
        transition={'background var(--tr-100-ei)'}
      >
        <Flex direction={'column'} gap={'6px'} flex={'1 1 auto'} minW={0} pl={'4px'}>
          <Box color={'{colors.textPrimary}'}>{note.text}</Box>
          <Flex fontSize={'14px'} gap={'4px'} color={'{colors.textTertiary}'}>
            <Box fontWeight={600}>Created:</Box>
            <Box>{getDateTimeString(note.created_at)}</Box>
            {note.created_at !== note.updated_at && (
              <>
                <Box fontWeight={600} pl={'4px'}>
                  Updated:
                </Box>
                <Box>{getDateTimeString(note.updated_at)}</Box>
              </>
            )}
          </Flex>
        </Flex>

        <Flex
          pr={'16px'}
          opacity={isLessThan768 ? 1 : 0}
          _groupHover={{opacity: 1}}
          transition={'opacity var(--tr-150-ei)'}
        >
          <QuickMenu
            hideActive
            container={containerRef}
            trigger={<Svg name={'Dots'} size={'s24'} />}
            items={[
              {
                id: 'edit',
                name: t('notes.actions.edit'),
                onClick: () => onEdit(note)
              },
              {
                id: 'delete',
                name: t('actions.delete'),
                isDanger: true,
                onClick: () => setConfirmDelete(true)
              }
            ]}
          />
        </Flex>
      </Flex>

      <ConfirmDialog
        isOpen={confirmDelete}
        title={t('notes.confirm.deleteTitle')}
        body={t('notes.confirm.deleteBody')}
        confirmLabel={t('actions.delete')}
        variant={'danger'}
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

export const NoteItem = NoteItemFC;
